import { useCallback, useMemo, useState } from 'react';
import {
  draftToJson,
  fromWireTool,
  parseBaseToolJson,
  toWireTool,
  validateDraft,
  type BaseToolDraft,
  type BaseToolWire,
  type DraftValidation,
} from '../lib/baseToolSchema';

export type JsonProblem = { reason: 'syntax'; message: string } | { reason: 'notAnObject' };

export interface BaseToolDraftState {
  draft: BaseToolDraft;
  /** Text of the JSON view. Follows the form, except while the user is editing it. */
  jsonText: string;
  /** Why `jsonText` could not be read; the draft keeps its last good state meanwhile. */
  jsonProblem: JsonProblem | null;
  /** Keys of the pasted JSON that the worker does not know. */
  unknownKeys: string[];
  validation: DraftValidation;
  /** Complete, valid, and the JSON view is readable. */
  canApply: boolean;
  wireTool: BaseToolWire;
  setField: (key: string, value: string) => void;
  setJsonText: (text: string) => void;
  clear: () => void;
  /** Start over from an applied tool (or from nothing). */
  reset: (tool: Record<string, unknown> | null) => void;
}

/** One base tool being edited, seen both as form fields and as JSON text. */
export function useBaseToolDraft(initialTool: Record<string, unknown> | null): BaseToolDraftState {
  const [draft, setDraft] = useState<BaseToolDraft>(() => fromWireTool(initialTool ?? {}).draft);
  const [jsonText, setJsonTextState] = useState(() => draftToJson(draft));
  const [jsonProblem, setJsonProblem] = useState<JsonProblem | null>(null);
  const [unknownKeys, setUnknownKeys] = useState<string[]>([]);

  const load = useCallback((next: BaseToolDraft) => {
    setDraft(next);
    setJsonTextState(draftToJson(next));
    setJsonProblem(null);
    setUnknownKeys([]);
  }, []);

  const setField = useCallback(
    (key: string, value: string) => {
      const next = { ...draft };
      if (value === '') delete next[key];
      else next[key] = value;
      load(next);
    },
    [draft, load]
  );

  const setJsonText = useCallback((text: string) => {
    setJsonTextState(text);
    const parsed = parseBaseToolJson(text);
    if (!parsed.ok) {
      setJsonProblem(
        parsed.reason === 'syntax'
          ? { reason: 'syntax', message: parsed.message }
          : { reason: 'notAnObject' }
      );
      return;
    }
    setJsonProblem(null);
    setDraft(parsed.draft);
    setUnknownKeys(parsed.unknownKeys);
  }, []);

  const clear = useCallback(() => load({}), [load]);
  const reset = useCallback(
    (tool: Record<string, unknown> | null) => load(fromWireTool(tool ?? {}).draft),
    [load]
  );

  const validation = useMemo(() => validateDraft(draft), [draft]);
  const wireTool = useMemo(() => toWireTool(draft), [draft]);

  return {
    draft,
    jsonText,
    jsonProblem,
    unknownKeys,
    validation,
    canApply: validation.isComplete && jsonProblem === null,
    wireTool,
    setField,
    setJsonText,
    clear,
    reset,
  };
}
